import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, Smartphone, X } from 'lucide-react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSetAsHome: () => Promise<void>;
  onSetAsMobile: () => Promise<void>;
  devicePlatform: string;
};

/**
 * Prompt que aparece cuando se detecta un nuevo dispositivo
 * Pregunta al usuario si es su dispositivo de casa o móvil
 */
export default function DeviceDetectionPrompt({ 
  isOpen, 
  onClose, 
  onSetAsHome, 
  onSetAsMobile,
  devicePlatform 
}: Props) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleSetAsHome = async () => {
    setIsProcessing(true);
    try {
      await onSetAsHome();
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSetAsMobile = async () => {
    setIsProcessing(true);
    try {
      await onSetAsMobile();
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('calls.deviceDetection.title', { defaultValue: '¿Es este tu dispositivo?' })}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Mensaje */}
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
            {t('calls.deviceDetection.message', { 
              defaultValue: 'Detectamos que estás usando un nuevo dispositivo. Para recibir llamadas correctamente, dinos qué tipo de dispositivo es:',
              platform: devicePlatform 
            })}
          </p>

          {/* Opciones */}
          <div className="space-y-3">
            {/* Dispositivo de Casa */}
            <button
              onClick={handleSetAsHome}
              disabled={isProcessing}
              className="w-full p-4 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-start gap-3">
                <Home className="w-6 h-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-left">
                  <div className="font-semibold text-gray-900 dark:text-white mb-1">
                    {t('calls.deviceDetection.homeDevice', { defaultValue: '🏠 Dispositivo de Casa' })}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {t('calls.deviceDetection.homeDeviceDesc', { 
                      defaultValue: 'Tablet o dispositivo fijo donde toda la familia puede recibir llamadas' 
                    })}
                  </div>
                </div>
              </div>
            </button>

            {/* Dispositivo Personal */}
            <button
              onClick={handleSetAsMobile}
              disabled={isProcessing}
              className="w-full p-4 rounded-xl border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-start gap-3">
                <Smartphone className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-left">
                  <div className="font-semibold text-gray-900 dark:text-white mb-1">
                    {t('calls.deviceDetection.mobileDevice', { defaultValue: '📱 Dispositivo Personal' })}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {t('calls.deviceDetection.mobileDeviceDesc', { 
                      defaultValue: 'Tu teléfono o dispositivo personal que solo tú usas' 
                    })}
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Nota */}
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center pt-2">
            {t('calls.deviceDetection.note', { 
              defaultValue: 'Puedes cambiar esto después en la configuración de llamadas' 
            })}
          </p>
        </div>
      </div>
    </div>
  );
}
